// @ts-nocheck
import {
  VictoryAccessibleGroup,
  VictoryAxis,
  VictoryChart,
  VictoryGroup,
  VictoryLabel,
  VictoryLegend,
  VictoryLine,
  VictoryScatter,
  VictoryTheme,
  VictoryTooltip
} from 'victory'
import { ToolTipLabelAverage } from '../../components/ToolTipLabel'
import { colorArray, getMonth, getSymbol } from 'utils'
import { DataPointType, MonthlyComparisonType } from '../../interfaces'
import { useState, useEffect, useId } from 'react'

const MonthlyComparison = ({ data, width, isIe }: MonthlyComparisonType) => {
  const [years, setYears] = useState<
    | {
      name?: string | undefined
      labels?:
      | {
        fill?: string | undefined
      }
      | undefined
      symbol?:
      | {
        fill?: string | undefined
        type?: string | undefined
      }
      | undefined
    }[]
    | undefined
  >()

  useEffect(() => {
    if (data?.length && !years) {
      const temp: {
        [index: string]: any
        Year: string | number
        Data: DataPointType[]
      }[] = []
      setYears(
        data
          .reduce((arr, curr) => {
            if (arr.some((x) => x.Year === curr.Year)) {
              return arr
            } else {
              return [...arr, curr]
            }
          }, temp)
          .map((y, i) => ({
            name: `${y.Year}`,
            symbol: { type: getSymbol(i) }
          }))
      )
    }
  }, [data, years])

  const isLarge = width > 600
  const id = useId()
  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        position: 'relative'
      }}
    >
      <VictoryChart
      desc='monthly comparison graph'
        theme={VictoryTheme.material}
        padding={{
          left: 70,
          right: isLarge ? 10 : 0,
          top: 40,
          bottom: isLarge ? 150 : 200
        }}
        width={width}
        height={isLarge ? 600 : 500}
        style={{
          parent: {
            width: '100%',
            height: isLarge ? 600 : 500
          }
        }}
      >
        {/* x axis */}
        <VictoryAxis
          orientation="bottom"
          tickValues={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]}
          tickFormat={(x) => (width > 600 ? (getMonth(x) ?? "") : getMonth(x)[0])}
          style={{
            axis: { stroke: 'transparent' },
            axisLabel: {
              fontSize: 16,
              fontFamily: 'Arial'
            },
            // @ts-ignore
            grid: { stroke: '#C7C7C7', strokeWidth: 0, strokeLength: 0 },
            ticks: { stroke: 'transparent' }
          }}
          tickLabelComponent={
            <VictoryLabel
              id={(i) => `x:${id}:${i.index}`}
              dy={10}
              // @ts-ignore
              style={{
                fontSize: isLarge ? 16 : 14,
                fontFamily: 'Arial',
                fontWeight: 'bold',
                fill: '#6E6E6E'
              }}
            />
          }
        />
        {/* y axis */}
        <VictoryAxis
          dependentAxis
          tickFormat={(x) => `${x} GL`}
          tickCount={isLarge ? 5 : 4}
          style={{
            axis: { stroke: 'transparent' },
            axisLabel: {
              fontSize: isLarge ? 16 : 14,
              fontFamily: 'Arial'
            },
            grid: { stroke: '#C7C7C7', strokeWidth: 0.8 },
            ticks: { stroke: 'transparent' },
            tickLabels: {
              fontSize: isLarge ? 16 : 14,
              fill: '#6E6E6E',
              fontFamily: 'Arial'
            }
          }}
          tickLabelComponent={
            <VictoryLabel
              id={(i) => `y:${id}:${i.index}`}
              style={{
                fontSize: 16,
                fill: '#6E6E6E',
                fontFamily: 'Arial',
                fontWeight: 'bold'
              }}
            />
          }
        />
        {/* lines from data */}
        {(data?.length ?? 0) > 0 && (
          <VictoryGroup
          groupComponent={<VictoryAccessibleGroup aria-label="monthly data points" />}
          >
            {data?.map((d, i) => (
              <VictoryLine
                key={`line-${i}`}
                data={d.Data}
                style={{
                  data: {
                    stroke: colorArray[i],
                    strokeWidth: () => (i === 0 ? 4 : 1)
                  }
                }}
              />
            ))}
            {!isIe &&
              data?.map((d, i) => (
                <VictoryScatter
                  key={`scatter-${i}`}
                  symbol={() => getSymbol(i)}
                  data={d.Data}
                  size={5}
                  style={{ data: { fill: colorArray[i] } }}
                  labels={(l) =>
                    `${getMonth(l.x)} Avg\n----\n${d.Year}: ${l.y}`
                  }
                  labelComponent={
                    <VictoryTooltip
                      cornerRadius={4}
                      style={{
                        fill: '#6E6E6E',
                        fontFamily: 'Arial',
                        fontSize: 16
                      }}
                      flyoutStyle={{
                        fill: 'white',
                        stroke: colorArray[i]
                      }}
                      labelComponent={
                        <ToolTipLabelAverage
                          year={d.Year as string}
                          color={colorArray[i]}
                        />
                      }
                      // @ts-ignore
                      pointerOrientation="bottom"
                      flyoutWidth={120}
                      flyoutHeight={50}
                      pointerWidth={122}
                    />
                  }
                />
              ))}
            {isIe &&
              data?.map((d, i) => (
                <VictoryScatter
                  key={`scatter-${i}`}
                  symbol={() => getSymbol(i)}
                  data={d.Data}
                  size={5}
                  style={{ data: { fill: colorArray[i] } }}
                  labels={() => ''}
                />
              ))}
          </VictoryGroup>
        )}
        {years && (
          // @ts-ignore
          <VictoryLegend
            x={isLarge ? width / 2 - 225 : width / 2 - 100}
            y={isLarge ? 520 : 345}
            orientation="horizontal"
            itemsPerRow={isLarge ? 4 : 3}
            gutter={isLarge ? 105 : 35}
            // @ts-ignore
            style={{ border: { stroke: 'none' } }}
            colorScale={colorArray}
            data={years}
            rowGutter={{ top: 0, bottom: isLarge ? 15 : 5 }}
            labelComponent={
              <VictoryLabel
                id={(i) => `ledg:${id}:${i.index}`}
                style={{
                  fontSize: isLarge ? 16 : 14,
                  fill: '#6E6E6E',
                  fontFamily: 'Arial'
                }}
              />
            }
          />
        )}
      </VictoryChart>
    </div>
  )
}

export default MonthlyComparison
